/**
 * Algolia Event Tracker
 * Handles all Algolia event tracking functionality
 */

import { AlgoliaEventData, ProductSpecData } from './interfaces';
import { AlgoliaConfigResolver } from './AlgoliaConfigResolver';
import { AlgoliaTokenManager } from './AlgoliaTokenManager';

export class AlgoliaEventTracker {
  private configResolver: AlgoliaConfigResolver;
  private tokenManager: AlgoliaTokenManager;

  constructor(configResolver: AlgoliaConfigResolver, tokenManager: AlgoliaTokenManager) {
    this.configResolver = configResolver;
    this.tokenManager = tokenManager;
  }

  /**
   * Common event creation and execution logic to reduce code duplication
   * @param eventName - The name of the event
   * @param eventType - The type of event (click, view, conversion)
   * @param objectIDs - Array of object IDs
   * @param algoliaInsightsClient - The Algolia insights client
   * @param additionalData - Additional event data
   * @param insightsMethod - The insights method to call
   */
  private executeEvent(
    eventName: string,
    eventType: string,
    objectIDs: string[],
    algoliaInsightsClient: any,
    additionalData: Partial<AlgoliaEventData> = {},
    insightsMethod: string
  ): void {
    const userTokenConfig = this.tokenManager.getUserTokenConfig();
    if (!userTokenConfig || !algoliaInsightsClient) {
      console.warn(`AlgoliaEventTracker not ready for ${eventType} tracking`);
      return;
    }

    // Split objectIDs into groups of 20 (Algolia's limit per event)
    const maxObjectsPerEvent = 20;
    const objectIDGroups = [];
    for (let i = 0; i < objectIDs.length; i += maxObjectsPerEvent) {
      objectIDGroups.push(objectIDs.slice(i, i + maxObjectsPerEvent));
    }

    // Send separate events for each group
    objectIDGroups.forEach((objectIDGroup) => {
      const eventData: AlgoliaEventData = {
        userToken: userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken
          ? userTokenConfig.authenticatedUserToken
          : userTokenConfig.userToken,
        eventName: eventName,
        index: this.configResolver.getProductIndex(),
        objectIDs: objectIDGroup,
        eventType: eventType,
        timestamp: Date.now(),
        ...additionalData
      };

      if (userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken) {
        eventData.authenticatedUserToken = userTokenConfig.authenticatedUserToken;
      }

      // Handle both v1 (function) and v2 (object) forms of insights client
      if (typeof algoliaInsightsClient === 'function') {
        algoliaInsightsClient(insightsMethod, eventData);
      } else if (algoliaInsightsClient && algoliaInsightsClient[insightsMethod]) {
        algoliaInsightsClient[insightsMethod](eventData);
      } else {
        console.error(`AlgoliaEventTracker: Unable to call ${insightsMethod} on insights client`);
      }
    });

    this.tokenManager.refreshTokenExpiration();
  }

  /**
   * Tracks a cart button click event for Algolia insights
   * @param algoliaQueryId - The Algolia query ID associated with the product
   * @param pzn - The product PZN (Pharmazentralnummer) identifier
   * @param source - The source of the cart action
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackCartButtonClick(
    algoliaQueryId: string,
    pzn: string,
    source: string = 'conversion',
    algoliaInsightsClient: any
  ): void {
    const userTokenConfig = this.tokenManager.getUserTokenConfig();
    if (!userTokenConfig || !algoliaInsightsClient) {
      console.warn('AlgoliaEventTracker not ready for cart button click tracking');
      return;
    }

    const index = this.configResolver.getProductIndex();
    const queryID = algoliaQueryId || '';
    const objectIDs = [pzn || ''];

    const eventData: AlgoliaEventData = {
      userToken: userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken
        ? userTokenConfig.authenticatedUserToken
        : userTokenConfig.userToken,
      eventName: 'Product Added To Cart',
      index: index,
      objectIDs: objectIDs,
      queryID: queryID,
      eventType: 'conversion',
      timestamp: Date.now(),
      data: {
        algoliaSource: [source]
      }
    };

    if (userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken) {
      eventData.authenticatedUserToken = userTokenConfig.authenticatedUserToken;
    }

    // Handle both v1 (function) and v2 (object) forms of insights client
    if (typeof algoliaInsightsClient === 'function') {
      algoliaInsightsClient('addedToCartObjectIDs', eventData);
    } else if (algoliaInsightsClient && algoliaInsightsClient.addedToCartObjectIDs) {
      algoliaInsightsClient.addedToCartObjectIDs(eventData);
    } else {
      console.error('AlgoliaEventTracker: Unable to call addedToCartObjectIDs on insights client');
    }
    // Refresh token expiration on user interaction
    this.tokenManager.refreshTokenExpiration();
  }

  /**
   * Tracks a purchase event for Algolia insights
   * @param products - Products array as JSON encoded object string
   * @param orderTotal - Order total value as string
   * @param source - The source of the purchase
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackPurchaseEvent(
    products: string,
    orderTotal: string,
    source: string = 'conversion',
    algoliaInsightsClient: any
  ): void {
    const userTokenConfig = this.tokenManager.getUserTokenConfig();
    if (!userTokenConfig || !algoliaInsightsClient) {
      console.warn('AlgoliaEventTracker not ready for purchase tracking');
      return;
    }

    const index = this.configResolver.getProductIndex();
    let productsObject: Record<string, {
      model: string;
      price: string;
      quantity: string;
      additional_properties: { algolia_query_id?: string };
    }>;

    try {
      productsObject = JSON.parse(products);
    } catch (error) {
      console.error('Error parsing products for purchase tracking:', error);
      return;
    }

    const objectIDs: string[] = [];
    const objectData: Array<{
      queryID?: string;
      price: number;
      quantity: number;
    }> = [];
    let eventQueryID: string | undefined;

    Object.keys(productsObject).forEach(key => {
      const value = productsObject[key];
      objectIDs.push(value.model);

      const objectDataItem: {
        queryID?: string;
        price: number;
        quantity: number;
      } = {
        price: parseFloat(value.price),
        quantity: Number(value.quantity)
      };

      if (value.additional_properties.algolia_query_id &&
          value.additional_properties.algolia_query_id.length > 0) {
        objectDataItem.queryID = value.additional_properties.algolia_query_id;

        // Use the first valid queryID as the event-level queryID
        if (!eventQueryID) {
          eventQueryID = value.additional_properties.algolia_query_id;
        }
      }

      objectData.push(objectDataItem);
    });

    const eventData: AlgoliaEventData = {
      userToken: userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken
        ? userTokenConfig.authenticatedUserToken
        : userTokenConfig.userToken,
      eventName: 'Products Purchased',
      eventSubtype: 'purchase',
      index: index,
      objectIDs: objectIDs,
      objectData: objectData,
      value: parseFloat(orderTotal),
      currency: 'EUR',
      eventType: 'conversion',
      timestamp: Date.now(),
      data: {
        algoliaSource: [source],
        context: 'checkout_success',
        testMode: !!(window as any).ORDER_PRODUCTS && (window as any).ORDER_PRODUCTS.includes('test-query-id')
      }
    };

    // Add event-level queryID if available
    if (eventQueryID) {
      eventData.queryID = eventQueryID;
    }

    if (userTokenConfig.isAuthenticated && userTokenConfig.authenticatedUserToken) {
      eventData.authenticatedUserToken = userTokenConfig.authenticatedUserToken;
    }

    // Handle both v1 (function) and v2 (object) forms of insights client
    if (typeof algoliaInsightsClient === 'function') {
      algoliaInsightsClient('purchasedObjectIDsAfterSearch', eventData);
    } else if (algoliaInsightsClient && algoliaInsightsClient.purchasedObjectIDsAfterSearch) {
      algoliaInsightsClient.purchasedObjectIDsAfterSearch(eventData);
    } else {
      console.error('AlgoliaEventTracker: Unable to call purchasedObjectIDsAfterSearch on insights client');
    }
    // Refresh token expiration on user interaction
    this.tokenManager.refreshTokenExpiration();
  }

  /**
   * Tracks a view event for Algolia insights
   * @param products - Array of product data to track
   * @param source - The source of the view
   * @param eventName - Custom event name
   * @param queryID - Optional queryID for attribution
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackViewEvent(
    products: ProductSpecData[],
    source: string = 'recommendation',
    eventName: string = 'Product Recommendation Viewed',
    queryID: string | undefined,
    algoliaInsightsClient: any
  ): void {
    const objectIDs = products.map(product => product.sku);
    const additionalData: Partial<AlgoliaEventData> = {
      data: {
        algoliaSource: [source]
      }
    };

    if (queryID) {
      additionalData.queryID = queryID;
    }

    this.executeEvent(eventName, 'view', objectIDs, algoliaInsightsClient, additionalData, 'viewedObjectIDs');
  }

  /**
   * Tracks a click event for Algolia insights
   * @param productSku - The SKU of the clicked product
   * @param source - The source of the click
   * @param eventName - Custom event name
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackClickEvent(
    productSku: string,
    source: string = 'recommendation',
    eventName: string = 'Product Recommendation Clicked',
    algoliaInsightsClient: any
  ): void {
    const additionalData: Partial<AlgoliaEventData> = {
      data: {
        algoliaSource: [source]
      }
    };

    this.executeEvent(eventName, 'click', [productSku], algoliaInsightsClient, additionalData, 'clickedObjectIDs');
  }

  /**
   * Tracks search result click events with position and InstantSearch-compatible signature
   * @param productSku - The SKU of the clicked product
   * @param queryID - The query ID from the search
   * @param position - The position of the product in search results (1-based)
   * @param eventName - Custom event name
   * @param query - The search query string
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackSearchResultClick(
    productSku: string,
    queryID: string | undefined,
    position: number | undefined,
    eventName: string = 'Click on Product Item | SRP',
    query: string | undefined,
    algoliaInsightsClient: any
  ): void {
    const additionalData: Partial<AlgoliaEventData> = {
      data: {
        algoliaSource: ['instantsearch'],
        ...(query && { query })
      }
    };

    if (queryID) {
      additionalData.queryID = queryID;
    }

    if (position !== undefined) {
      additionalData.positions = [position];
    }

    this.executeEvent(eventName, 'click', [productSku], algoliaInsightsClient, additionalData, 'clickedObjectIDs');
  }

  /**
   * Tracks search result view events (page views/hits viewed)
   * @param productSkus - Array of product SKUs that were viewed
   * @param queryID - The query ID from the search
   * @param eventName - Custom event name
   * @param query - The search query string
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackSearchResultView(
    productSkus: string[],
    queryID: string | undefined,
    eventName: string = 'Hits Viewed',
    query: string | undefined,
    algoliaInsightsClient: any
  ): void {
    const additionalData: Partial<AlgoliaEventData> = {
      data: {
        algoliaSource: ['instantsearch'],
        ...(query && { query })
      }
    };

    if (queryID) {
      additionalData.queryID = queryID;
    }

    this.executeEvent(eventName, 'view', productSkus, algoliaInsightsClient, additionalData, 'viewedObjectIDs');
  }

  /**
   * Tracks autosuggest click events
   * @param productSku - The SKU of the clicked product
   * @param queryID - The query ID from the search
   * @param position - The position of the product in autosuggest results (1-based)
   * @param eventName - Custom event name
   * @param query - The search query string
   * @param algoliaInsightsClient - The Algolia insights client
   */
  public trackAutosuggestClick(
    productSku: string,
    queryID: string | undefined,
    position: number | undefined,
    eventName: string = 'Click on Product List Item | Autosuggest',
    query: string | undefined,
    algoliaInsightsClient: any
  ): void {
    const additionalData: Partial<AlgoliaEventData> = {
      data: {
        algoliaSource: ['instantsearch'],
        ...(query && { query })
      }
    };

    if (queryID) {
      additionalData.queryID = queryID;
    }

    if (position !== undefined) {
      additionalData.positions = [position];
    }

    this.executeEvent(eventName, 'click', [productSku], algoliaInsightsClient, additionalData, 'clickedObjectIDs');
  }

}